import React, { useEffect, useRef, useState } from "react";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader, EmptyState } from "@/components/Common";
import { toast } from "sonner";
import {
  Plus, Trash2, Pencil, ImagePlus, Upload, X, ArrowUp, ArrowDown, ImageOff,
} from "lucide-react";

const EMPTY = {
  image_file_id: null, title: "", subtitle: "",
  cta_label: "", cta_url: "", order: 0, is_active: true,
};

const IMG_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_MB = 8;

async function uploadImage(file) {
  if (!IMG_TYPES.includes(file.type)) throw new Error("Only JPG, PNG or WEBP allowed");
  if (file.size > MAX_MB * 1024 * 1024) throw new Error(`Image is larger than ${MAX_MB} MB`);
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/files/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.id;
}

function ImagePicker({ imageFileId, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const preview = imageFileId ? fileUrl(imageFileId) : null;
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const id = await uploadImage(f);
      onChange(id);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.message || err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };
  return (
    <div>
      <input ref={ref} type="file" accept={IMG_TYPES.join(",")} onChange={onFile}
        className="hidden" data-testid="slide-file-input" />
      {preview ? (
        <div className="relative rounded-lg border border-gray-200 overflow-hidden bg-gray-100">
          <img src={preview} alt="slide" className="w-full aspect-[21/9] object-cover" />
          <div className="absolute top-2 right-2 flex gap-1.5">
            <Button size="sm" variant="secondary" onClick={() => ref.current?.click()}
              disabled={busy} className="h-8 gap-1 shadow" data-testid="slide-image-change">
              <Upload size={12} /> {busy ? "Uploading…" : "Change"}
            </Button>
            <Button size="sm" variant="destructive" onClick={() => onChange(null)}
              className="h-8 gap-1 shadow" data-testid="slide-image-remove">
              <X size={12} /> Remove
            </Button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} disabled={busy}
          data-testid="slide-image-upload"
          className="w-full aspect-[21/9] rounded-lg border-2 border-dashed border-gray-300
                     bg-gray-50 hover:bg-emerald-50 hover:border-emerald-500 transition-colors
                     flex flex-col items-center justify-center text-gray-500 hover:text-emerald-700">
          <ImagePlus size={30} />
          <span className="text-sm mt-2 font-medium">{busy ? "Uploading…" : "Browse a wide banner image"}</span>
          <span className="text-[11px] text-gray-400 mt-1">Recommended 1920×820 · JPG/PNG/WEBP up to {MAX_MB} MB</span>
        </button>
      )}
    </div>
  );
}

function SlideDialog({ open, onClose, initial, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.id;
  useEffect(() => { setForm(initial || EMPTY); }, [initial]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.image_file_id) { toast.error("Please upload a banner image first"); return; }
    setBusy(true);
    try {
      const payload = {
        image_file_id: form.image_file_id,
        title: form.title || "",
        subtitle: form.subtitle || "",
        cta_label: form.cta_label || "",
        cta_url: form.cta_url || "",
        order: Number(form.order) || 0,
        is_active: form.is_active !== false,
      };
      if (isEdit) await api.patch(`/hero-slides/${initial.id}`, payload);
      else await api.post("/hero-slides", payload);
      toast.success(isEdit ? "Slide updated" : "Slide added");
      onSaved(); onClose();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{isEdit ? "Edit slide" : "Add slide"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <ImagePicker imageFileId={form.image_file_id} onChange={(id) => set("image_file_id", id)} />
          <div>
            <Label className="text-xs">Headline (optional)</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)}
              placeholder="e.g. Fresh groceries, delivered daily"
              data-testid="slide-title-input" />
          </div>
          <div>
            <Label className="text-xs">Subtext (optional)</Label>
            <Input value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)}
              placeholder="e.g. Free delivery on orders above UGX 50,000"
              data-testid="slide-subtitle-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Button label (optional)</Label>
              <Input value={form.cta_label} onChange={(e) => set("cta_label", e.target.value)}
                placeholder="Shop now" data-testid="slide-cta-label-input" />
            </div>
            <div>
              <Label className="text-xs">Button link</Label>
              <Input value={form.cta_url} onChange={(e) => set("cta_url", e.target.value)}
                placeholder="/site/shop  or  https://…"
                data-testid="slide-cta-url-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Order (lower = shows first)</Label>
              <Input type="number" value={form.order}
                onChange={(e) => set("order", e.target.value)}
                data-testid="slide-order-input" />
            </div>
            <div className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2">
              <div>
                <div className="text-sm font-medium">Active</div>
                <div className="text-[11px] text-gray-500">Hide without deleting.</div>
              </div>
              <Switch checked={form.is_active !== false}
                onCheckedChange={(v) => set("is_active", v)}
                data-testid="slide-active-switch" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !form.image_file_id}
            className="bg-emerald-900 hover:bg-emerald-950 text-white"
            data-testid="slide-submit-button">
            {busy ? "Saving…" : (isEdit ? "Save changes" : "Add slide")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function HeroSlides() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hero-slides");
      // sort by (order asc, created_at asc)
      data.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (s) => {
    if (!window.confirm("Delete this slide?")) return;
    try { await api.delete(`/hero-slides/${s.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed"); }
  };

  const toggleActive = async (s) => {
    try { await api.patch(`/hero-slides/${s.id}`, { is_active: s.is_active === false }); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Update failed"); }
  };

  const move = async (idx, dir) => {
    const swap = idx + dir;
    if (swap < 0 || swap >= items.length) return;
    const a = items[idx], b = items[swap];
    const ao = a.order ?? idx, bo = b.order ?? swap;
    try {
      await Promise.all([
        api.patch(`/hero-slides/${a.id}`, { order: bo }),
        api.patch(`/hero-slides/${b.id}`, { order: ao }),
      ]);
      load();
    } catch (e) { toast.error("Reorder failed"); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Website"
        title="Homepage sliding banners"
        subtitle="These images rotate in the hero at the top of the public homepage. Upload wide banners (recommended 1920×820)."
        actions={
          <Button className="bg-emerald-900 hover:bg-emerald-950 text-white"
            onClick={() => setEditing({})} data-testid="slide-new-button">
            <Plus size={16} className="mr-1.5" /> Add slide
          </Button>
        }
      />
      {loading ? (
        <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-md">
          <EmptyState title="No slides yet"
            hint="Click 'Add slide' to upload your first homepage banner." />
        </div>
      ) : (
        <div className="space-y-3" data-testid="slide-list">
          {items.map((s, idx) => (
            <div key={s.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col md:flex-row"
              data-testid={`slide-row-${s.id}`}>
              <div className="md:w-64 shrink-0 bg-gray-100 aspect-[21/9] md:aspect-auto md:h-auto relative">
                {s.image_file_id ? (
                  <img src={fileUrl(s.image_file_id)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-300">
                    <ImageOff size={28} />
                  </div>
                )}
              </div>
              <div className="flex-1 p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-gray-900 truncate">
                      {s.title || <span className="text-gray-400">Untitled slide</span>}
                    </div>
                    {s.subtitle && <div className="text-sm text-gray-600 truncate">{s.subtitle}</div>}
                    <div className="flex items-center gap-2 mt-1.5 text-[11px] text-gray-500">
                      <span>Position #{(s.order ?? 0) + 1}</span>
                      {s.cta_url && <span>· CTA: <code className="text-gray-700">{s.cta_url}</code></span>}
                    </div>
                  </div>
                  <Badge className={s.is_active === false
                    ? "bg-gray-400 hover:bg-gray-400" : "bg-emerald-700 hover:bg-emerald-700"}>
                    {s.is_active === false ? "Hidden" : "Active"}
                  </Badge>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-gray-100">
                  <Button size="sm" variant="outline" disabled={idx === 0}
                    onClick={() => move(idx, -1)} data-testid={`slide-up-${s.id}`}>
                    <ArrowUp size={14} />
                  </Button>
                  <Button size="sm" variant="outline" disabled={idx === items.length - 1}
                    onClick={() => move(idx, 1)} data-testid={`slide-down-${s.id}`}>
                    <ArrowDown size={14} />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(s)}
                    data-testid={`slide-edit-${s.id}`}>
                    <Pencil size={12} className="mr-1" /> Edit
                  </Button>
                  <div className="flex items-center gap-2 ml-auto">
                    <span className="text-xs text-gray-500">Active</span>
                    <Switch checked={s.is_active !== false} onCheckedChange={() => toggleActive(s)}
                      data-testid={`slide-toggle-${s.id}`} />
                    <Button size="sm" variant="ghost" onClick={() => remove(s)}
                      data-testid={`slide-delete-${s.id}`}
                      className="text-red-700 hover:text-red-800 hover:bg-red-50">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <SlideDialog open={editing !== null} onClose={() => setEditing(null)}
        initial={editing?.id ? editing : null} onSaved={load} />
    </div>
  );
}
